import React, { FunctionComponent } from 'react';
import { Button } from 'react-bootstrap/lib';
import { BootstrapTable, TableHeaderColumn } from 'react-bootstrap-table';
import { groupBy } from 'lodash';
import { History } from 'history';
import { Dealer } from '@upstart/harmony/lib/legos/dealerTypes';
import { FormPack } from '@upstart/harmony/lib/legos/formTypes';
import ErrorMessage from '../ErrorMessage';
import {
  FINANCE_TYPE_GROUP,
  CONDITION_AND_TRADE_GROUP,
  PROTECTION_PLANS_GROUP,
  getOnlineFormPackMap,
} from '../../containers/OnlineFormPack/constants';
import './OnlineFormPackList.scss';

const FINANCE_TYPE_TITLE = 'Purchase Type Form Packs';
const CONDITION_AND_TRADE_TYPE_TITLE = 'Condition And Trade Form Packs';
const PROTECTION_PLANS_TYPE_TITLE = 'Protection Plan Packs';

interface Props {
  dealer: Dealer;
  list: FormPack[];
  error: string | null;
  history: History;
}

const OnlineFormPackList: FunctionComponent<Props> = ({
  dealer,
  list,
  error,
  history,
}) => {
  const actionFormatter = (_cell: object, row: FormPack) => (
    <div>
      <Button
        key={row.id}
        id={`split-button-basic-${row.id}`}
        onClick={() => {
          // Database record exists, inform the history
          if (row.createdAt) {
            history.push(
              `/${row.dealer_id}/onlineFormPacks/onlineFormPack/${row.id}`,
              { recordExists: true },
            );
          } else {
            // Database record does not exist, inform the history
            history.push(
              `/${row.dealer_id}/onlineFormPacks/onlineFormPack/${row.id}`,
              { recordExists: false },
            );
          }
        }}
      >
        Edit
      </Button>
    </div>
  );

  const templateFormatter = (_cell: object, row: FormPack) => {
    const { FormTemplates } = row;
    return (
      <div>
        {FormTemplates?.map(form => (
          <div key={form.name || ''}> {form.name} </div>
        ))}
      </div>
    );
  };

  const createTable = (title: string, data: FormPack[]) => {
    // Hide the Add button if we are an Online Form Pack
    const options = {
      // BootstrapTable requires that we return ReactElement, but we dont want to render
      // anything here so use an empty fragment
      // eslint-disable-next-line
      insertBtn: () => <></>,
    };
    return (
      <>
        <h3>{title}</h3>
        <BootstrapTable
          data={data}
          options={options}
          insertRow
          striped
          hover
          bodyContainerClass="list-body-container"
          tableStyle={{ border: 'none' }}
        >
          <TableHeaderColumn isKey dataField="name" dataSort>
            Name
          </TableHeaderColumn>
          <TableHeaderColumn
            dataField="FormTemplates"
            dataFormat={templateFormatter}
          >
            Forms
          </TableHeaderColumn>
          <TableHeaderColumn
            dataField="button"
            columnClassName="list-action-column"
            dataFormat={actionFormatter}
          />
        </BootstrapTable>
      </>
    );
  };

  // Get all form packs incl. Protection Plans
  const onlineFormPackMap = getOnlineFormPackMap(dealer);

  // Get a dict of formPacks
  const groupedLists = groupBy(list, (item: FormPack) => {
    // Get short key without the dealer prefix
    const allGroups = Object.keys(onlineFormPackMap);
    const groupName = allGroups.find(g => item.id.includes(g));
    // @ts-ignore
    return onlineFormPackMap[groupName].group;
  });

  return (
    <div className="form-pack-list-container">
      <div>
        Please note, multiple form packs may be combined as long as the
        conditions of their sale meet the conditions of the form pack.
      </div>
      {error && <ErrorMessage error={error} />}
      {createTable(FINANCE_TYPE_TITLE, groupedLists[FINANCE_TYPE_GROUP])}
      {createTable(
        CONDITION_AND_TRADE_TYPE_TITLE,
        groupedLists[CONDITION_AND_TRADE_GROUP],
      )}
      {createTable(
        PROTECTION_PLANS_TYPE_TITLE,
        groupedLists[PROTECTION_PLANS_GROUP],
      )}
    </div>
  );
};

export default OnlineFormPackList;
